import { parseInt, cloneDeep, isEmpty, isNil } from 'lodash';

import { dataFields } from './dataFields';
import { isVideoFile } from '../utilities/videoHelpers';
import {
	DEFAULT_AUDIO_THUMBNAIL,
	IMAGE_ALLOWED_EXTENSIONS,
	IMAGE_ALLOWED_MIME_TYPES,
} from './sections/constants';

export const changeLayout = async (store, layout, actingAsState) => {
	const workAddress = actingAsState?.addresses?.find(
		(address) => address.type === 'work',
	);

	const userCountry = workAddress ? workAddress.country : 'US';

	const isGlobalRole = actingAsState?.isGlobalRole ?? false;

	const minPages = isGlobalRole
		? layout.etc.cfg.global.minPages
		: layout.etc.cfg.customer.minPages;

	const maxPages = isGlobalRole
		? layout.etc.cfg.global.maxPages
		: layout.etc.cfg.customer.maxPages;

	//NOTE: cfg.order.itemId this could change in the future
	const isCountrySpecific = isGlobalRole
		? false
		: !isNil(layout.etc.cfg.order.countries) &&
			layout.etc.cfg.order.countries.length > 0;

	store.setUnit({
		unit: layout.json.unit,
		dpi: layout.json.dpi,
	});

	store.setSize(layout.json.width, layout.json.height, true);

	if (minPages && store.pages.length < minPages) {
		for (let page = store.pages.length; page < minPages; page++) {
			store.addPage();
		}
	}

	if (maxPages && store.pages.length > maxPages) {
		const pageIds = [];

		for (let page = store.pages.length; page > maxPages; page--) {
			pageIds.push(store.pages[page - 1].id);
		}

		if (pageIds.length > 0) {
			store.deletePages(pageIds);
		}
	}

	if (isCountrySpecific) {
		store.pages.forEach((page) => {
			if (
				!isNil(page.custom) &&
				!isNil(page.custom.country) &&
				page.custom.country !== userCountry
			) {
				store.deletePages([page.id]);
			}
		});
	}

	return;
};

export const escapeRegExp = (val) => {
	const reRegExpChar = /[\\^$.*+?()[{}|]/g;

	const str = val?.toString?.() ?? val;

	if (str === undefined) {
		throw new TypeError(`${val} can't be stringified`);
	}

	return RegExp(reRegExpChar.source).test(str)
		? str.replace(reRegExpChar, '[$&]')
		: str;
};

export const formatPhoneNumber = (phoneNumberString) => {
	const cleaned = `${phoneNumberString}`.replace(/\D/g, '');
	const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

	if (match) {
		const intlCode = match[1] ? '+1 ' : '';
		return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
	}

	return null;
};

export const hexToRGB = (h) => {
	const hexCode = h.replace('#', '');
	let rgb = [];

	if (hexCode.length === 6) {
		rgb = hexCode.match(/.{1,2}/g).map((code) => {
			return parseInt(`${code}`, 16);
		});
	}

	if (rgb.length === 0) {
		return null;
	}

	return { r: rgb[0], g: rgb[1], b: rgb[2], a: 1 };
};

export const getSessionState = (stateName) => {
	try {
		const persistedState = window.sessionStorage.getItem(stateName);

		if (persistedState) {
			return JSON.parse(persistedState);
		} else {
			return;
		}
	} catch (e) {
		console.log(e);
	}
};

export const setSessionState = (stateName, state) => {
	try {
		return window.sessionStorage.setItem(stateName, JSON.stringify(state));
	} catch (e) {
		console.log('Storage error, attempting to evict data!', e);
		window.sessionStorage.removeItem(stateName);
	}
};

export const svgToPng = (svgUrl) => {
	return new Promise((resolve) => {
		try {
			const container = document.createElement('div');
			container.setAttribute('style', 'visibility: hidden;background: white;');
			document.body.appendChild(container);

			const svgImage = document.createElement('img');
			container.appendChild(svgImage);

			svgImage.onload = () => {
				const canvas = document.createElement('canvas');
				canvas.width = svgImage.clientWidth;
				canvas.height = svgImage.clientHeight;
				const canvasCtx = canvas.getContext('2d');
				canvasCtx.drawImage(svgImage, 0, 0);
				const imgData = canvas.toDataURL('image/png');
				container.remove();
				resolve(imgData);
			};

			svgImage.src = svgUrl;
		} catch (e) {
			resolve(svgUrl);
		}
	});
};

const IMAGE_PROXY_URL = 'https://imageproxy.services.bkat.io/';

// The image proxy exists to fetch and crop a *remote* image.
const isLocalImageSrc = (src) =>
	typeof src === 'string' && /^(data:|blob:)/i.test(src);

// A color directive whose tag did not resolve still contains `{{…}}`; never write that into a layer.
const hasResolvedColorValue = (value) =>
	typeof value === 'string' &&
	value.trim() !== '' &&
	!value.includes('{{') &&
	!value.includes('}}');

const resetImageCrop = (layer) => {
	// The image proxy handles the cropping, so clear the layer's own crop or
	// moving the image around would shift the placement again.
	layer.cropWidth = 1;
	layer.cropHeight = 1;
	layer.cropX = 0;
	layer.cropY = 0;
};

const applyTextDirective = (layer, directive, data) => {
	layer.text =
		processTemplate(
			layer.text,
			directive.text,
			data,
			directive.format,
			directive.maxLen,
		) ?? layer.text;

	const nextFill = processTemplate(layer.fill, directive.fill, data);
	if (hasResolvedColorValue(nextFill)) {
		layer.fill = nextFill;
	}

	const nextColor = processTemplate(layer.color, directive.color, data);
	if (hasResolvedColorValue(nextColor)) {
		layer.color = nextColor;
		layer.fill = nextColor;
	}
};

const applyLayerDirective = (layer, directive, data) => {
	if (!directive) {
		return layer;
	}

	if (layer.type === 'text') {
		applyTextDirective(layer, directive, data);
	}

	if (layer.type === 'svg') {
		layer.src = processTemplate(layer.src, directive.src, data) ?? layer.src;

		// Apply brand color directives to SVGs.
		if (directive.color) {
			const nextColor =
				processTemplate('', directive.color, data) || directive.color;

			if (
				hasResolvedColorValue(nextColor) &&
				typeof layer.src === 'string' &&
				layer.src.startsWith('data:image/svg+xml;base64,')
			) {
				layer.src = svgSetColor(layer.src, nextColor);
			}
		}
	}

	if (layer.type === 'figure') {
		const nextColor = processTemplate(
			layer.fill || layer.color,
			directive.color,
			data,
		);
		if (hasResolvedColorValue(nextColor)) {
			layer.fill = nextColor;
			layer.stroke = nextColor;
			layer.color = nextColor;
		}
	}

	if (layer.type === 'image') {
		const previousSrc = layer.src;

		layer.src = processTemplate(layer.src, directive.src, data) || layer.src;

		// An unfilled slot is left untouched: its source is the layer's own placeholder or an
		// already-proxied URL, and proxying either would wrap it again on every re-template.
		const isProxyable =
			Boolean(directive.src) &&
			processTemplate('', directive.src, data) !== '' &&
			typeof layer.src === 'string' &&
			layer.src !== '' &&
			!isLocalImageSrc(layer.src) &&
			!layer.src.startsWith(IMAGE_PROXY_URL);

		if (
			isProxyable &&
			(directive.src.indexOf('user.image') !== -1 ||
				directive.src.indexOf('user.logo') !== -1)
		) {
			layer.src = `${IMAGE_PROXY_URL}?w=${layer.width}&h=${layer.height}&fit=contain&url=${encodeURIComponent(layer.src)}&`;
			resetImageCrop(layer);
		}

		if (isProxyable && directive.src.indexOf('listing.image') !== -1) {
			// An unfilled slot keeps its old image, so it keeps its old flag (none for a template placeholder)
			layer.custom = {
				...layer.custom,
				listingImageSrc:
					layer.src !== previousSrc ? layer.src : layer.custom?.listingImageSrc,
			};
			layer.src = `${IMAGE_PROXY_URL}?w=${layer.width}&h=${layer.height}&fit=cover&a=attention&url=${encodeURIComponent(layer.src)}`;
			resetImageCrop(layer);
		}
	}

	return layer;
};

export const createJsonFromData = (
	json,
	directives,
	data,
	activePageId,
	replaceAllPages = false,
) => {
	if (!directives) {
		return json;
	}

	const jsonData = cloneDeep(json);
	const activePageIndex = jsonData.pages.findIndex(
		(page) => page.id === activePageId,
	);

	const parsePage = (page) => {
		page.children = page.children.map((layer) =>
			applyLayerDirective(layer, directives[layer.id], data),
		);
	};

	if (activePageIndex > -1 && !replaceAllPages) {
		parsePage(jsonData.pages[activePageIndex]);
	} else {
		jsonData.pages.forEach(parsePage);
	}

	return jsonData;
};

// Patches only the layers whose id is a key in `directives`, without walking
// or re-templating any other layer in the design. Use this instead of
// createJsonFromData when a single directive needs to be re-applied
// (e.g. backfilling a directive added after the design JSON was baked).
export const patchLayersFromDirectives = (json, directives, data) => {
	if (!directives || isEmpty(directives)) {
		return json;
	}

	const jsonData = cloneDeep(json);

	jsonData.pages.forEach((page) => {
		page.children = (page.children || []).map((layer) => {
			const directive = directives[layer.id];
			if (!directive) {
				return layer;
			}

			if (layer.type === 'text') {
				applyTextDirective(layer, directive, data);
			}

			if (layer.type === 'svg' && directive.src) {
				layer.src =
					processTemplate(layer.src, directive.src, data) ?? layer.src;
			}

			return layer;
		});
	});

	return jsonData;
};

const flattenData = (data) => {
	try {
		let newData = {};

		for (const field in data) {
			if (Array.isArray(data[field])) {
				data[field].forEach((record, index) => {
					newData = {
						...newData,
						[`${field}_${index}`]: record,
					};
				});
			} else if (typeof data[field] === 'object') {
				for (const key in data[field]) {
					if (Object.prototype.hasOwnProperty.call(data[field], key)) {
						newData[`${field}_${key}`] = data[field][key];
					}
				}
			} else {
				newData[field] = data[field];
			}
		}

		return newData;
	} catch (e) {
		console.error('Flattening data failed, nothing was mutated.', e);
		return data;
	}
};

export const processTemplate = (currentValue, template, data, format, maxLen) => {
	if (!template || template === '') {
		return;
	}

	const replacements = template.match(/{{(.+?)}}/g);
	// Design Studio sends preferences.phoneNumberFormat; BayPolotno's Previewer sends preferences.phoneFormat.
	const phoneNumberFormat =
		data?.preferences?.phoneNumberFormat ||
		data?.preferences?.phoneFormat ||
		'default';

	if (replacements) {
		let defaultValue = currentValue;

		if (replacements.length > 1) {
			defaultValue = '';
		}

		replacements.forEach((replacement) => {
			const token = replacement.replace(/{{|}}/g, '');

			if (!dataFields[token]) return;

			const dataField = dataFields[token].dataField;
			const dataFieldParts = dataField.split('.');
			let dataKey = dataField;
			let d = data || null;

			const ns = dataFieldParts.length > 1 ? dataFieldParts[0] : null;

			if (ns) {
				d = data?.[ns] || null;
				dataKey = dataField.replace(`${ns}.`, '');
			}

			{
				// An absent namespace (e.g. no listing in data) is treated like a missing key.
				if (d) d = flattenData(d);

				let value;

				if (d && Object.hasOwn(d, dataKey)) {
					// Field exists in data - use it (0 and false render; nil/blank fall through below)
					value = d[dataKey];
					if (dataField === 'user.websiteUrl' && value) {
						value = value
							.trim()
							.replace(/^https?:\/\//i, '')
							.replace(/^www\./i, '')
							.replace(/\/{2,}$/g, '/')
							.replace(/\/+$/g, '');
					}
				}

				if (
					isNil(value) ||
					(typeof value === 'string' && value.trim() === '')
				) {
					// A missing, absent or blank field: a bare tag keeps the layer's default verbatim
					// (no format/maxLen applied) so the element is not blanked out; a tag inside
					// static text is emptied so the broken partial string is not shown.
					if (template.trim() === replacement) {
						template = defaultValue;
						return;
					}
					value = '';
				}

				//some values will be integers, so indexOf will not work
				if (
					!isNil(value) &&
					typeof value === 'string' &&
					value.indexOf('data:image/svg+xml;base64,') !== -1
				) {
					template = svgSetColor(defaultValue, value);
				} else {
					value = templateStringFormat(
						value,
						format,
						maxLen,
						phoneNumberFormat,
						dataKey,
					);

					template = template.replaceAll(replacement, value);
				}
			}
		});
	}

	return template;
};

const getFormatPhoneNumber = (value, match, phoneFormatPreference) => {
	if (!match) return value;

	const intl = match[1] ? `+${match[1]} ` : '';

	switch (phoneFormatPreference) {
		case 'dash':
			return `${intl}${match[2]}-${match[3]}-${match[4]}`;

		case 'dot':
			return `${intl}${match[2]}.${match[3]}.${match[4]}`;

		case 'parentheses':
		case 'default':
		default:
			return `${intl}(${match[2]}) ${match[3]}-${match[4]}`;
	}
};

const templateStringFormat = (
	value,
	format,
	maxLen,
	phoneNumberFormat = 'default',
	dataKey = '',
) => {
	// No value to process, abort
	if (isNil(value) || value === '') {
		return shortenText('', maxLen);
	}

	const cleaned = `${value}`.replace(/\D/g, '');
	const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
	const parts = value.toString().toLowerCase().split(' ');

	switch (format) {
		case 'number':
		case 'dollar':
		case 'dollarCents':
		case 'decimal': {
			if (typeof value === 'string' && /[a-zA-Z]/.test(value)) {
				return value;
			}
			if (typeof value === 'string' && value.trim() === '') {
				return '';
			}
			const isNegative = value.toString().trim().startsWith('-');
			const numericStr = value.toString().replace(/[^0-9.]/g, '');
			if (numericStr === '') {
				return '';
			}
			const magnitude = numericStr.includes('.')
				? parseFloat(numericStr)
				: parseInt(numericStr, 10);
			if (isNaN(magnitude)) {
				return '';
			}
			const num = isNegative ? -magnitude : magnitude;
			if (format === 'dollar' || format === 'dollarCents') {
				return num.toLocaleString('en-US', {
					style: 'currency',
					currency: 'USD',
					minimumFractionDigits: format === 'dollarCents' ? 2 : 0,
					maximumFractionDigits: format === 'dollarCents' ? 2 : 0,
				});
			}
			return num.toLocaleString('en-US', {
				minimumFractionDigits: format === 'decimal' ? 2 : 0,
			});
		}

		case 'phoneNumber':
			return getFormatPhoneNumber(value, match, phoneNumberFormat);

		case 'uppercase':
			return value.toString().toUpperCase();

		case 'lowercase':
			return value.toString().toLowerCase();

		case 'titlecase':
			return parts
				.map((part) => {
					return part.charAt(0).toUpperCase() + part.slice(1);
				})
				.join(' ');

		default:
			return shortenText(value, maxLen, dataKey);
	}
};

export const shortenText = (input, maxLen, dataKey = '') => {
	// Only null/undefined are empty; 0 should render as "0"
	if (input === null || input === undefined) return '';

	const str = input.toString();

	if (!maxLen || maxLen === 0) return str;
	if (typeof input === 'number') return str.slice(0, maxLen);

	const sentences = str.match(/[^.!?]+[.!?]+/g);

	// Acreage and baths are decimals, not sentences: "2.5" must not be cut at the dot.
	if (!sentences || dataKey === 'acreage' || dataKey === 'baths') {
		return str.slice(0, maxLen);
	}

	let totalLen = 0;
	const truncated = [];

	for (const sentence of sentences) {
		//if the first element is longer then the maxLen, we need to shorten that sentence.
		if (truncated.length === 0 && sentence.trim().length > maxLen) {
			return sentence.slice(0, maxLen);
		}

		const s = (truncated.length !== 0 ? ' ' : '') + sentence.trim();

		if (totalLen + s.length <= maxLen) {
			truncated.push(s);
			totalLen += s.length;
		} else {
			break;
		}
	}

	return truncated.join('');
};

export const svgSetColor = (svg, color) => {
	try {
		svg = svgDataUriToXML(svg);

		svg = svg.replace(/fill="[^"]+"/g, `fill="${color}"`);
		svg = svg.replace(/stroke="[^"]+"/g, `stroke="${color}"`);

		return `data:image/svg+xml;base64,${btoa(svg)}`;
	} catch (e) {
		return svg;
	}
};

const colorToRGBA = (input) => {
	// console.log('colorToRGBA', 'Before', input);

	let m,
		r,
		g,
		b,
		a = 1;

	if (input && input?.indexOf('rgba') !== -1) {
		m = input.match(
			/^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*(\d*(?:\.\d+)?)\)$/i,
		);

		if (!m) return;

		r = parseInt(m[1]);
		g = parseInt(m[2]);
		b = parseInt(m[3]);
		a = parseFloat(m[4]);
	} else if (input && input?.indexOf('rgb') !== -1) {
		m = input.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);

		if (!m) return;

		r = parseInt(m[1]);
		g = parseInt(m[2]);
		b = parseInt(m[3]);
	} else if (input && input?.indexOf('#') !== -1) {
		input = input.replace('#', '');

		r = parseInt(input.slice(0, 2), 16);
		g = parseInt(input.slice(2, 4), 16);
		b = parseInt(input.slice(4, 6), 16);
	} else {
		// console.log('colorToRGBA', 'After', input);
		return input;
	}

	const rgba = `rgba(${r}, ${g}, ${b}, ${a})`;

	// console.log('colorToRGBA', 'After', rgba);

	return rgba;
};

export const fixSVGLayers = (json) => {
	const jsonData = cloneDeep(json);
	jsonData.pages.map((page) => {
		return page.children.map((layer) => {
			// `qr` is the name the QR layer is created with; its colors are managed by the QR tool.
			if (layer.type === 'svg' && layer.name !== 'qr') {
				layer.src = svgColorToRGBA(layer.src);

				if (!isEmpty(layer.colorsReplace)) {
					layer.colorsReplace = fixColorsReplace(layer.colorsReplace);
				}
			}
			return layer;
		});
	});

	return jsonData;
};

export const fixColorsReplace = (colorsReplace) => {
	const replacements = {};

	for (const replaceKey in colorsReplace) {
		const key = colorToRGBA(replaceKey);
		const value = colorToRGBA(colorsReplace[replaceKey]);

		// console.log(replaceKey, key, value, colorsReplace[replaceKey])

		replacements[key] = value;
	}

	return replacements;
};

export const svgDataUriToXML = (svg) => {
	const isBase64Svg = svg.indexOf('data:image/svg+xml;base64,') !== -1;

	// Only a base64 SVG data URI (or a bare base64 payload) can be decoded; a remote
	// URL, a blob: URL or a utf8 data URI is returned as-is instead of throwing in atob.
	if (!isBase64Svg && /^(https?:|blob:|data:)/i.test(svg)) {
		return svg;
	}

	if (isBase64Svg) {
		svg = svg.split('base64,')[1];
	}

	const svgXML = atob(svg).match(/<svg[^>]*>(.|\n)*<\/svg>/g);

	return Array.isArray(svgXML) ? svgXML[0] : svg;
};

export const svgColorToRGBA = (svg) => {
	let svgXML = svgDataUriToXML(svg);

	// console.log('svgColorToRGBA', svgXML);

	if (svgXML.indexOf('fill="') !== -1) {
		svgXML.match(/fill="[^"]+"/g).forEach((fill) => {
			const fillColor = fill.replace('fill="', '').replace('"', '');

			svgXML = svgXML.replaceAll(
				`fill="${fillColor}"`,
				`fill="${colorToRGBA(fillColor)}"`,
			);
		});
	}

	if (svgXML.indexOf('stroke="') !== -1) {
		svgXML.match(/stroke="[^"]+"/g).forEach((stroke) => {
			const strokeColor = stroke.replace('fill="', '').replace('"', '');

			svgXML = svgXML.replaceAll(
				`stroke="${strokeColor}"`,
				`stroke="${colorToRGBA(strokeColor)}"`,
			);
		});
	}

	return `data:image/svg+xml;base64,${btoa(svgXML)}`;
};

export const colorNameToHex = (colorName) => {
	const colorNames = {
		aliceblue: '#F0F8FF',
		antiquewhite: '#FAEBD7',
		aqua: '#00FFFF',
		aquamarine: '#7FFFD4',
		azure: '#F0FFFF',
		beige: '#F5F5DC',
		bisque: '#FFE4C4',
		black: '#000000',
		blanchedalmond: '#FFEBCD',
		blue: '#0000FF',
		blueviolet: '#8A2BE2',
		brown: '#A52A2A',
		burlywood: '#DEB887',
		cadetblue: '#5F9EA0',
		chartreuse: '#7FFF00',
		chocolate: '#D2691E',
		coral: '#FF7F50',
		cornflowerblue: '#6495ED',
		cornsilk: '#FFF8DC',
		crimson: '#DC143C',
		cyan: '#00FFFF',
		darkblue: '#00008B',
		darkcyan: '#008B8B',
		darkgoldenrod: '#B8860B',
		darkgray: '#A9A9A9',
		darkgrey: '#A9A9A9',
		darkgreen: '#006400',
		darkkhaki: '#BDB76B',
		darkmagenta: '#8B008B',
		darkolivegreen: '#556B2F',
		darkorange: '#FF8C00',
		darkorchid: '#9932CC',
		darkred: '#8B0000',
		darksalmon: '#E9967A',
		darkseagreen: '#8FBC8F',
		darkslateblue: '#483D8B',
		darkslategray: '#2F4F4F',
		darkslategrey: '#2F4F4F',
		darkturquoise: '#00CED1',
		darkviolet: '#9400D3',
		deeppink: '#FF1493',
		deepskyblue: '#00BFFF',
		dimgray: '#696969',
		dimgrey: '#696969',
		dodgerblue: '#1E90FF',
		firebrick: '#B22222',
		floralwhite: '#FFFAF0',
		forestgreen: '#228B22',
		fuchsia: '#FF00FF',
		gainsboro: '#DCDCDC',
		ghostwhite: '#F8F8FF',
		gold: '#FFD700',
		goldenrod: '#DAA520',
		gray: '#808080',
		grey: '#808080',
		green: '#008000',
		greenyellow: '#ADFF2F',
		honeydew: '#F0FFF0',
		hotpink: '#FF69B4',
		indianred: '#CD5C5C',
		indigo: '#4B0082',
		ivory: '#FFFFF0',
		khaki: '#F0E68C',
		lavender: '#E6E6FA',
		lavenderblush: '#FFF0F5',
		lawngreen: '#7CFC00',
		lemonchiffon: '#FFFACD',
		lightblue: '#ADD8E6',
		lightcoral: '#F08080',
		lightcyan: '#E0FFFF',
		lightgoldenrodyellow: '#FAFAD2',
		lightgray: '#D3D3D3',
		lightgrey: '#D3D3D3',
		lightgreen: '#90EE90',
		lightpink: '#FFB6C1',
		lightsalmon: '#FFA07A',
		lightseagreen: '#20B2AA',
		lightskyblue: '#87CEFA',
		lightslategray: '#778899',
		lightslategrey: '#778899',
		lightsteelblue: '#B0C4DE',
		lightyellow: '#FFFFE0',
		lime: '#00FF00',
		limegreen: '#32CD32',
		linen: '#FAF0E6',
		magenta: '#FF00FF',
		maroon: '#800000',
		mediumaquamarine: '#66CDAA',
		mediumblue: '#0000CD',
		mediumorchid: '#BA55D3',
		mediumpurple: '#9370DB',
		mediumseagreen: '#3CB371',
		mediumslateblue: '#7B68EE',
		mediumspringgreen: '#00FA9A',
		mediumturquoise: '#48D1CC',
		mediumvioletred: '#C71585',
		midnightblue: '#191970',
		mintcream: '#F5FFFA',
		mistyrose: '#FFE4E1',
		moccasin: '#FFE4B5',
		navajowhite: '#FFDEAD',
		navy: '#000080',
		oldlace: '#FDF5E6',
		olive: '#808000',
		olivedrab: '#6B8E23',
		orange: '#FFA500',
		orangered: '#FF4500',
		orchid: '#DA70D6',
		palegoldenrod: '#EEE8AA',
		palegreen: '#98FB98',
		paleturquoise: '#AFEEEE',
		palevioletred: '#DB7093',
		papayawhip: '#FFEFD5',
		peachpuff: '#FFDAB9',
		peru: '#CD853F',
		pink: '#FFC0CB',
		plum: '#DDA0DD',
		powderblue: '#B0E0E6',
		purple: '#800080',
		rebeccapurple: '#663399',
		red: '#FF0000',
		rosybrown: '#BC8F8F',
		royalblue: '#4169E1',
		saddlebrown: '#8B4513',
		salmon: '#FA8072',
		sandybrown: '#F4A460',
		seagreen: '#2E8B57',
		seashell: '#FFF5EE',
		sienna: '#A0522D',
		silver: '#C0C0C0',
		skyblue: '#87CEEB',
		slateblue: '#6A5ACD',
		slategray: '#708090',
		slategrey: '#708090',
		snow: '#FFFAFA',
		springgreen: '#00FF7F',
		steelblue: '#4682B4',
		tan: '#D2B48C',
		teal: '#008080',
		thistle: '#D8BFD8',
		tomato: '#FF6347',
		turquoise: '#40E0D0',
		violet: '#EE82EE',
		wheat: '#F5DEB3',
		white: '#FFFFFF',
		whitesmoke: '#F5F5F5',
		yellow: '#FFFF00',
		yellowgreen: '#9ACD32',
	};

	// eslint-disable-next-line no-prototype-builtins
	if (!colorNames.hasOwnProperty(colorName.toLowerCase())) {
		throw new Error('Invalid color name');
	}

	const hexColor = colorNames[colorName.toLowerCase()];

	return hexColor;
};

export const MEDIA_TYPE_FILTERS = {
	image: (item) => item?.type !== 'video' && item?.type !== 'audio',
	video: (item) => item?.type === 'video',
	audio: (item) => item?.type === 'audio',
};

export const toDurationSeconds = (value) => {
	const raw = Number(value);
	if (!Number.isFinite(raw) || raw <= 0) return null;
	return Math.round(raw);
};

export const formatDuration = (seconds) => {
	const totalSeconds = Math.max(0, Number(seconds) || 0);
	const mins = Math.floor(totalSeconds / 60);
	const secs = totalSeconds % 60;
	return `${mins}:${String(secs).padStart(2, '0')}`;
};

export const normalizeUploadedFile = (res) => {
	if (!res?.result) {
		return null;
	}

	const toNormalizedFile = (result) => {
		if (!result) return null;

		return {
			id: result._id,
			name: result.name,
			type: result.mimeType.startsWith('video')
				? 'video'
				: result.mimeType.startsWith('audio')
					? 'audio'
					: result.mimeType === 'image/gif'
						? 'gif'
						: 'image',
			mimeType: result.mimeType,
			size: result.size,
			src: result.url,
			thumbnail: result.mimeType.startsWith('audio')
				? DEFAULT_AUDIO_THUMBNAIL
				: result.thumbnailUrls?.custom ||
					result.thumbnailUrls?.default ||
					result.url,
			createdAt: result.dtIns,
		};
	};

	if (Array.isArray(res.result)) { // Handle array of uploaded files
		return res.result.map(toNormalizedFile).filter(Boolean);
	}

	return toNormalizedFile(res.result);
};

export const getPersistableMedia = (mediaItems) => {
	return (mediaItems || []).filter(
		(item) => typeof item?.src === 'string' && !item.src.startsWith('data:'),
	);
};

export const getCheckedImageFiles = async (files) => {
	const checkedFiles = await Promise.all(
		files.map(async (file) => {
			const isVideo = await isVideoFile(file);
			const fileName = file.name.toLowerCase();
			const hasValidMimeType =
				file.type.startsWith('image/') &&
				IMAGE_ALLOWED_MIME_TYPES.includes(file.type);
			const hasValidExtension = IMAGE_ALLOWED_EXTENSIONS.some((extension) =>
				fileName.endsWith(extension),
			);

			return {
				file,
				isValidImage: hasValidMimeType && hasValidExtension && !isVideo,
			};
		}),
	);

	return {
		validFiles: checkedFiles
			.filter(({ isValidImage }) => isValidImage)
			.map(({ file }) => file),
		invalidFiles: checkedFiles
			.filter(({ isValidImage }) => !isValidImage)
			.map(({ file }) => file),
	};
};

export const probeMediaDuration = (type, src, timeoutMs = 15000) => {
	return new Promise((resolve, reject) => {
		if (!src || (type !== 'audio' && type !== 'video')) {
			reject(new Error('Invalid media type or source'));
			return;
		}

		const mediaEl = document.createElement(type);
		mediaEl.preload = 'metadata';

		const cleanup = () => {
			mediaEl.onloadedmetadata = null;
			mediaEl.onerror = null;
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			mediaEl.removeAttribute('src');
			mediaEl.load();
		};

		mediaEl.onloadedmetadata = () => {
			const duration = toDurationSeconds(mediaEl.duration);
			cleanup();

			if (!duration) {
				reject(new Error('Invalid media duration'));
				return;
			}

			resolve(duration);
		};

		mediaEl.onerror = () => {
			cleanup();
			reject(new Error('Failed to load media metadata'));
		};

		const timeoutId = setTimeout(() => {
			cleanup();
			reject(new Error('Media metadata loading timed out'));
		}, timeoutMs);

		mediaEl.src = src;
	});
};

export const probeVideoMetadata = (src, timeoutMs = 30000) => {
	return new Promise((resolve, reject) => {
		if (!src) {
			reject(new Error('Invalid video source'));
			return;
		}

		const tempVideo = document.createElement('video');
		tempVideo.preload = 'metadata';

		let settled = false;

		const cleanup = () => {
			tempVideo.onloadedmetadata = null;
			tempVideo.onerror = null;
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			tempVideo.removeAttribute('src');
			tempVideo.load();
			tempVideo.remove();
		};

		const fail = (message) => {
			if (settled) return;
			settled = true;
			cleanup();
			reject(new Error(message));
		};

		tempVideo.onloadedmetadata = () => {
			if (settled) return;

			const duration = tempVideo.duration;
			const width = tempVideo.videoWidth;
			const height = tempVideo.videoHeight;

			if (
				!Number.isFinite(duration) ||
				duration <= 0 ||
				!Number.isFinite(width) ||
				width <= 0 ||
				!Number.isFinite(height) ||
				height <= 0
			) {
				fail('Failed to load video metadata');
				return;
			}

			settled = true;
			cleanup();
			resolve({
				durationMs: duration * 1000,
				width,
				height,
			});
		};

		tempVideo.onerror = () => {
			fail('Failed to load video metadata');
		};

		const timeoutId = setTimeout(() => {
			fail('Video metadata loading timed out');
		}, timeoutMs);

		tempVideo.src = src;
		tempVideo.load();
	});
};
